import { useEffect, useRef } from "react";
import { emitTo, listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import {
  getCurrentOwnerEpoch,
  getCurrentOwnerLabel,
} from "@/lib/owner-state-client";
import { useCompanionStore } from "@/stores/companion-store";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  isRuntimeType,
  personalRuntime,
} from "@/desktop/onboarding/post-demo-runtime-preference";
import { safeTauriUnlisten } from "@/desktop/tauri-listener-cleanup";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import type { RuntimeHealthIssue } from "./runtime-health";
import type { RuntimeHealthActions } from "./use-runtime-health-actions";

export const RUNTIME_ACTION_REQUESTED_EVENT = "denker://runtime-health-request";

interface RuntimeActionIntent {
  kind: RuntimeHealthIssue["kind"];
  runtimeType: RuntimeHealthIssue["runtimeType"];
}

interface RuntimeActionEvent extends RuntimeActionIntent {
  authScope: DesktopChatAuthScope;
  ownerEpoch: number;
}

/** Settings on any display send intent; executable paths stay owner-local. */
export async function requestRuntimeHealthAction(
  issue: RuntimeHealthIssue,
): Promise<void> {
  const owner = getCurrentOwnerLabel();
  const authScope = getDesktopChatAuthScope();
  if (!isTauri() || !owner || !authScope)
    throw new Error("Denker is unavailable. Reopen settings to try again.");
  await emitTo(owner, RUNTIME_ACTION_REQUESTED_EVENT, {
    kind: issue.kind,
    runtimeType: issue.runtimeType,
    authScope,
    ownerEpoch: getCurrentOwnerEpoch(),
  } satisfies RuntimeActionEvent);
}

function currentIssue(intent: RuntimeActionIntent): RuntimeHealthIssue | null {
  const runtimes = useCompanionStore.getState().availableRuntimes;
  const runtime = personalRuntime(runtimes, intent.runtimeType);
  if (intent.kind === "missing")
    return runtimes.some((candidate) => candidate.type === intent.runtimeType)
      ? null
      : { kind: "missing", runtimeType: intent.runtimeType };
  if (!runtime) return null;
  if (intent.kind === "signed_out" && runtime.logged_in === false)
    return { kind: "signed_out", runtimeType: intent.runtimeType, runtime };
  if (
    intent.kind === "update" &&
    runtime.update_available &&
    runtime.update_command
  )
    return { kind: "update", runtimeType: intent.runtimeType, runtime };
  return null;
}

/** The owner keeps setup and its progress alive after the settings pocket closes. */
export function useRuntimeHealthActionRequests(
  actions: RuntimeHealthActions,
): void {
  const latest = useRef(actions);
  latest.current = actions;
  useEffect(() => {
    let active = true;
    let pending = false;
    const isCurrent = (request: RuntimeActionEvent) =>
      active &&
      getWindowRoleSync() === "owner" &&
      request.ownerEpoch === getCurrentOwnerEpoch() &&
      isCurrentDesktopChatAuthScope(request.authScope);
    const registration = listen<RuntimeActionEvent>(
      RUNTIME_ACTION_REQUESTED_EVENT,
      ({ payload }) => {
        if (
          !payload ||
          typeof payload.runtimeType !== "string" ||
          !isRuntimeType(payload.runtimeType) ||
          !["missing", "signed_out", "update"].includes(payload.kind) ||
          !isCurrent(payload) ||
          pending ||
          latest.current.setupActive
        )
          return;
        const issue = currentIssue(payload);
        if (!issue) return;
        pending = true;
        void latest.current
          .resolveMode(issue)
          .then((mode) => {
            if (!isCurrent(payload) || latest.current.setupActive) return;
            const fresh = currentIssue(payload);
            if (
              !fresh ||
              ("runtime" in issue &&
                (!("runtime" in fresh) ||
                  fresh.runtime.path !== issue.runtime.path))
            )
              return;
            latest.current.runRuntimeAction(fresh, mode);
          })
          .catch(() => {})
          .finally(() => {
            pending = false;
          });
      },
    ).catch(() => null);
    return () => {
      active = false;
      void registration.then((unlisten) => {
        if (unlisten) safeTauriUnlisten(unlisten);
      });
    };
  }, []);
}
